import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";

export const ProductVendorSelector = memo(function ProductVendorSelector({
  selectedVendors,
  onVendorsSelected,
}: {
  selectedVendors: string[];
  onVendorsSelected: (vendors: string[]) => void;
}) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const vendorsLoad = useLoad(
    async (signal) => {
      try {
        const response = await merchantRpcClient.getProductVendors(null, {
          signal,
        });

        if (!response.vendors) {
          return [];
        }

        return response.vendors;
      } catch (error) {
        console.error("Error searching product vendors:", error);
        return [];
      }
    },
    [merchantRpcClient],
  );

  const vendorOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!vendorsLoad.value) {
      return [];
    }

    // Load all vendors from backend then do search filtering on frontend
    const loadedVendors = vendorsLoad.value.filter((vendor) =>
      vendor.toLowerCase().includes(searchString.toLowerCase()),
    );

    const allVendors = new Set([...loadedVendors, ...selectedVendors]);

    return Array.from(allVendors).map((vendor) => ({
      id: vendor,
      value: vendor,
      type: "badge",
      badge: { text: vendor, color: "gray" },
    }));
  }, [vendorsLoad.value, selectedVendors, searchString]);

  const selectedVendorOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return selectedVendors.map((vendor) => ({
      id: vendor,
      value: vendor,
      type: "badge",
      badge: { text: vendor, color: "gray" },
    }));
  }, [selectedVendors]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      onVendorsSelected(items.map((item) => item.value));
    },
  );

  return (
    <Flex w="full">
      <RedoTagsDropdownInput<string>
        description={
          selectedVendors.length > 0
            ? t("{{count}} vendors selected", { count: selectedVendors.length })
            : undefined
        }
        loading={vendorsLoad.pending}
        options={vendorOptions}
        placeholder={t("Enter or select vendors")}
        searchPlaceholder={t("Search for product vendors...")}
        searchString={searchString}
        selectedOptions={selectedVendorOptions}
        setSearchString={setSearchString}
        setSelectedOptions={handleSelectionChange}
        size="xs"
      />
    </Flex>
  );
});
